import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import ModalLoader from '@components/modal/ModalLoader'
import Modal from '@components/modal/Modal'
import SimpleAlert from '@components/modal/SimpleAlert'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import Drawer from '@components/drawer/Drawer'

import Layout from './ControleFormLayout'
import Delete from './forms/Delete'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

import useReducer from '@store/useReducer'
import * as ControleStore from '@store/controle/controle'
import * as UserStore from '@store/user/user'
import * as SiteStore from '@store/site/site'

import api from '@api/api'

import useWorkflow from './useWorkflow'
import { SCREENS } from './workflow'

import * as uuid from '@utils/uuid'
import { paramsToSearch } from '@api/utils'

import {
  ControleQualitePeriode,
  ControleQualiteNature,
  ControleQualiteTypeControle,
  ControleQualiteConformite,
  TypeUtilisateur,
} from '@api/enum'

interface Props {
  id?: string
  formStep?: ControleStep
  manifestationId?: string
}

const ControleFormScreen = ({ id, formStep, manifestationId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const forms = useReducer(ControleStore.store, (s) => s.forms)
  const form = useReducer(ControleStore.store, (s) => s.form)
  const user = useReducer(UserStore.store, (s) => s.user!)
  const site = useReducer(SiteStore.store, (s) => s.site!)

  const [step, setStep] = React.useState(0)

  const options = { manifestationId }

  const [workflow, starter] = useWorkflow(form, user, options)

  const previous = React.useMemo(
    () => forms.filter((f) => f.siteId === site.id).sort((a, b) => b.creationDate!.localeCompare(a.creationDate!)),
    [forms, site]
  )

  React.useEffect(() => {
    ControleStore.actions.setForm(starter)
  }, [starter])

  React.useEffect(() => {
    // register last screen visited for every form
    if (step > 0) {
      ControleStore.actions.setForm({ step: workflow[step] })
    }
  }, [step])

  React.useEffect(() => {
    if (form) {
      // save form in drafts when change
      if (form.id && (form.societeId || form.manifestationId)) {
        const next = [...forms.filter((f) => f.id !== form.id), form]
        ControleStore.actions.setForms(next)
      }

      // update draft if manifestation but no id
      if (form.manifestationId && !form.id) {
        // complete form initialize
        ControleStore.actions.setForm({
          id: uuid.createUUID(),
          siteId: site.id,
          creationDate: new Date().toISOString(),
        })
      }
    }
  }, [form])

  React.useEffect(() => {
    if (id) {
      const prev = forms.find((f) => !!id && f.id === id)

      if (prev) {
        ControleStore.actions.setForm(prev)
      }
    }
  }, [forms, id])

  React.useEffect(() => {
    const idx = workflow.findIndex((w) => w === formStep)

    if (idx > -1) {
      setStep(idx)
    }
  }, [formStep, workflow])

  const onStart = (item?: Partial<ControleForm>) => {
    ControleStore.actions.resetForm()

    if (item) {
      // already started, we set current
      ControleStore.actions.setForm({ ...starter, ...item })
    } else {
      // new form
      ControleStore.actions.setForm({
        ...starter,
        id: uuid.createUUID(),
        siteId: site.id,
        creationDate: new Date().toISOString(),
      })
    }

    // return to last visited step, or go to first step
    if (item && item.step && item.step !== 'start') {
      navigation.push(`/controles/form/${item.step}${paramsToSearch(options)}`)
    } else {
      onNext()
    }

    return Promise.resolve()
  }

  const onForm = (item: Partial<ControleForm>) => {
    ControleStore.actions.setForm({ ...form, ...item })
    return Promise.resolve()
  }

  const onNext = () => {
    if (step === workflow.length - 1) {
      onFinish()
    } else {
      const next = workflow[step + 1] || ''
      navigation.push(`/controles/form/${next}${paramsToSearch(options)}`)
    }
  }

  const onBack = () => {
    if (step === 0) {
      navigation.goBack()
    } else {
      const previous = workflow[step - 1] || ''
      navigation.push(`/controles/form/${previous}${paramsToSearch(options)}`)
    }
  }

  const onDelete = (id: string) => {
    ControleStore.actions.removeForm(id)
    ControleStore.actions.resetForm()

    Drawer.close()
  }

  const deleteIncident = () => {
    Drawer.open({
      render: () => <Delete data={previous} onClose={Drawer.close} onDelete={onDelete} />,
      hideTarget: true,
    })
  }

  const quit = () => {
    navigation.jumpTo(`/controles`)
  }

  const finish = () => {
    Modal.close()
    quit()

    ControleStore.actions.removeForm(form.id)
    ControleStore.actions.resetForm()
  }

  const onFinish = () => {
    ModalLoader.open({ title: i18n.t('screens.controles.form.sending') })

    const body: ControleQualiteCreationRequest = {
      metierId: form.metierId,
      societeId: form.societeId,
      manifestationId: form.nature === 'PRESTATION' ? form.manifestationId : undefined,
      period: form.nature === 'PRESTATION' && form.period ? ControleQualitePeriode[form.period] : undefined,
      nature: ControleQualiteNature[form.nature],
      type:
        user.typeUtilisateur === TypeUtilisateur.VIPARIS
          ? ControleQualiteTypeControle.VIPARIS
          : ControleQualiteTypeControle.AUTO,
      accompagnementPrestataire: !!form.accompagnementPrestataire,
      commentaire: form.commentaire,
      reponses: form.reponses.map((r) => ({ ...r, conformite: ControleQualiteConformite[r.conformite] })),
    }

    api.controle
      .create(body)
      .then(() => {
        ModalLoader.close()
        Modal.open({ Content: renderFinish, cancel: false })
      })
      .catch(() => {
        ModalLoader.close()

        SimpleAlert.open({
          title: i18n.t('common.error'),
          description: i18n.t('screens.controles.form.sendingFailed'),
          buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
        })
      })
  }

  const renderFinish = () => {
    return (
      <FinishContainer>
        <Title>{i18n.t('screens.controles.form.sent')}</Title>
        <Info>{i18n.t('screens.controles.form.sentInfo')}</Info>
        <ButtonContainer>
          <Button label={i18n.t('common.finish')} onClick={finish} status="active" />
        </ButtonContainer>

        <Close activeOpacity={0.8} onPress={finish}>
          <Icon name="close" color={theme.colors.primary} size={25} />
        </Close>
      </FinishContainer>
    )
  }

  const screen = workflow[step]

  if (!screen) {
    return <Root left="close" onLeft={quit} header={i18n.t('screens.controles.form.title')} fixed loading />
  }

  const renderForm = (scr: ControleStep) => {
    const FormScreen = SCREENS[scr]

    switch (scr) {
      case 'start':
        return (
          <ItemContainer key={scr}>
            <FormScreen form={form} onForm={onStart} onBack={onBack} />
          </ItemContainer>
        )

      case 'spaces':
      case 'questions':
        return (
          <ItemContainer key={scr}>
            <Layout step={scr} form={form} onBack={onBack} onNext={onNext} onQuit={quit} full>
              <FormScreen form={form} onForm={onForm} onBack={onBack} onQuit={quit} />
            </Layout>
          </ItemContainer>
        )

      case 'recap':
        return (
          <ItemContainer key={scr}>
            <Layout step={scr} form={form} onBack={onBack} onNext={onNext} onQuit={quit} full last>
              <FormScreen form={form} onForm={onForm} onBack={onBack} />
            </Layout>
          </ItemContainer>
        )

      default:
        return (
          <ItemContainer key={scr}>
            <Layout step={scr} form={form} onBack={onBack} onNext={onNext} onQuit={quit}>
              <FormScreen form={form} onForm={onForm} onBack={onBack} />
            </Layout>
          </ItemContainer>
        )
    }
  }

  return (
    <Root
      left="close"
      onLeft={quit}
      right={step === 0 ? 'delete' : undefined}
      onRight={step === 0 ? deleteIncident : undefined}
      header={i18n.t('screens.controles.title')}
      fixed>
      {renderForm(screen)}
    </Root>
  )
}

export default ControleFormScreen

const ItemContainer = styled(View)`
  flex: 1;
`

const FinishContainer = styled(View)`
  position: relative;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px;' : '50px 140px')};
  max-width: 880px;
  background-color: ${(props) => props.theme.colors.background};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  right: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  padding: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 24px;
  text-align: center;
`

const ButtonContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 270px;`}
  margin: 10px;
  align-self: center;
`
